import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { changeLanguage, getCurrentLanguage, type SupportedLanguage } from '@/i18n';

import flagBR from '@/assets/flags/br.webp';
import flagUS from '@/assets/flags/us.png';
import flagES from '@/assets/flags/es.jpg';
import flagFR from '@/assets/flags/fr.jpg';

const langs: { code: SupportedLanguage; label: string; flag: string }[] = [
  { code: 'pt-BR', label: 'PT', flag: flagBR },
  { code: 'en-US', label: 'EN', flag: flagUS },
  { code: 'es-ES', label: 'ES', flag: flagES },
  { code: 'fr-FR', label: 'FR', flag: flagFR },
];

interface LangSelectorProps {
  variant?: 'desktop' | 'mobile';
}

const LangSelector = ({ variant = 'desktop' }: LangSelectorProps) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentLang = getCurrentLanguage();
  const currentLabel = langs.find(l => l.code === currentLang)?.label || 'PT';
  const currentFlag = langs.find(l => l.code === currentLang)?.flag || flagBR;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = async (code: SupportedLanguage) => {
    await changeLanguage(code);
    // PRIORIDADE 4: Update html lang dynamically
    document.documentElement.lang = code;
    setOpen(false);
  };

  if (variant === 'mobile') {
    return (
      <div className="flex items-center gap-6 mt-4" role="group" aria-label="Language selector">
        {langs.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            aria-label={`Switch language to ${lang.label}`}
            aria-current={currentLang === lang.code ? 'true' : undefined}
            className={`flex items-center gap-2 text-sm tracking-[0.15em] uppercase transition-opacity duration-200 min-w-[48px] min-h-[48px] ${
              currentLang === lang.code ? 'text-foreground opacity-100' : 'text-muted-foreground opacity-50 hover:opacity-100'
            }`}
          >
            <img src={lang.flag} alt="" className="w-5 h-5 rounded-full object-cover" width={20} height={20} />
            {lang.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" role="group" aria-label="Language selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-opacity duration-200 opacity-60 hover:opacity-100 min-w-[48px] min-h-[48px] px-1"
        aria-label={`Language: ${currentLabel}. Click to change.`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <img src={currentFlag} alt="" className="w-4 h-4 rounded-full object-cover" width={16} height={16} />
        <span className="text-[10px] sm:text-xs tracking-[0.15em] uppercase font-light"
          style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}
        >
          {currentLabel}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} strokeWidth={1} />
      </button>

      <div
        role="listbox"
        aria-label="Available languages"
        className={`absolute top-[calc(100%+12px)] right-0 min-w-[80px] py-2 border border-border/30 transition-all duration-250 ${
          open
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
        style={{ background: 'hsl(0 0% 5%)' }}
      >
        {langs.map((lang) => (
          <button
            key={lang.code}
            role="option"
            aria-selected={currentLang === lang.code}
            onClick={() => handleSelect(lang.code)}
            className={`flex items-center gap-3 w-full text-left px-4 py-2 text-[11px] tracking-[0.15em] uppercase transition-colors duration-200 cursor-pointer min-h-[44px] ${
              currentLang === lang.code
                ? 'text-foreground'
                : 'text-muted-foreground/50 hover:text-foreground'
            }`}
            style={{ fontFamily: "'Montserrat', system-ui, sans-serif", fontWeight: 300 }}
          >
            <img src={lang.flag} alt="" className="w-4 h-4 rounded-full object-cover" width={16} height={16} />
            {lang.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LangSelector;
